import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import { productService } from '../services'
import useAuthStore from '../store/authStore'
import { canExportData } from '../utils/adminPermissions'
import { UPLOAD_HISTORY_POLL_MS } from '../constants/uploadHistoryPolling'
import { formatDateTime } from '../utils/dateFormat'

const STATUS_STYLES = {
  queued: { bgcolor: '#e3f2fd', color: '#1565c0', label: 'Queued' },
  processing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  preparing: { bgcolor: '#fff3e0', color: '#e65100', label: 'Preparing' },
  completed: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  ready: { bgcolor: '#e8f5e9', color: '#1b5e20', label: 'Ready' },
  failed: { bgcolor: '#ffebee', color: '#c62828', label: 'Failed' },
  no_data: { bgcolor: '#eceff1', color: '#455a64', label: 'No data' },
  expired: { bgcolor: '#eceff1', color: '#546e7a', label: 'Expired' },
}

function scopeSummary(job) {
  const parts = []
  if (job.search) parts.push(`Search: “${job.search}”`)
  if (job.downloadAll) parts.push('All matching')
  else parts.push(`Limit ${job.rowLimit || '—'}`)
  return parts.join(' · ') || '—'
}

function optionsSummary(job) {
  const parts = ['Products']
  if (job.includeBarcodes) parts.push('Barcodes')
  if (job.includePricing) parts.push('Pricing')
  return parts.join(', ')
}

function StatusChip({ status, displayStatus }) {
  const key =
    displayStatus === 'no_data'
      ? 'no_data'
      : status === 'processing' || displayStatus === 'preparing'
        ? 'preparing'
        : status === 'completed' || displayStatus === 'ready'
          ? 'ready'
          : displayStatus || status || 'failed'
  const style = STATUS_STYLES[key] || STATUS_STYLES.failed
  return (
    <Chip
      size="small"
      label={style.label}
      sx={{
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: 'none',
      }}
    />
  )
}

const ProductDownloadsPage = () => {
  const navigate = useNavigate()
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const showDownloadExcel = canExportData(adminPermissions)
  const [jobs, setJobs] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [retryingId, setRetryingId] = useState(null)

  const hasActiveJobs = useMemo(
    () =>
      jobs.some((job) => {
        const status = job?.status
        const display = job?.displayStatus
        return (
          status === 'queued' ||
          status === 'processing' ||
          display === 'preparing' ||
          display === 'queued'
        )
      }),
    [jobs],
  )

  const fetchJobs = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true)
        setError(null)
        const response = await productService.getDownloadJobs({
          page: page + 1,
          limit: rowsPerPage,
        })
        setJobs(response.items || [])
        setTotal(response.pagination?.total || 0)
      } catch (err) {
        if (!silent) {
          setError(
            err.response?.data?.message ||
              'Could not load Product Downloads. Check your connection and try again.',
          )
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [page, rowsPerPage],
  )

  useEffect(() => {
    fetchJobs()
  }, [fetchJobs])

  useEffect(() => {
    if (!hasActiveJobs) return undefined
    const id = setInterval(() => {
      fetchJobs({ silent: true })
    }, UPLOAD_HISTORY_POLL_MS)
    return () => clearInterval(id)
  }, [hasActiveJobs, fetchJobs])

  const handleDownload = async (job) => {
    if (!job.canDownload) {
      setToast(
        job.status === 'expired'
          ? 'This file expired after 7 days. Start a new download from Manage Products.'
          : job.status === 'failed'
            ? job.errorMessage || 'This export failed. Start a new download from Manage Products.'
            : 'Your file is still being prepared. Wait until status is Ready.',
      )
      return
    }

    try {
      setDownloadingId(job.id)
      const { downloadUrl, fileName } = await productService.getDownloadJobUrl(job.id)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = fileName || 'products-export.xlsx'
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setToast('Download started.')
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          'Could not download this file. Refresh and try again.',
      )
    } finally {
      setDownloadingId(null)
    }
  }

  const handleRetry = async (job) => {
    try {
      setRetryingId(job.id)
      const response = await productService.createDownloadJob({
        selectedColumns: job.selectedColumns?.length
          ? job.selectedColumns
          : ['product_name', 'sku'],
        downloadAll: Boolean(job.downloadAll),
        limit: job.downloadAll ? undefined : job.rowLimit || 100,
        search: job.search || undefined,
        includeBarcodes: Boolean(job.includeBarcodes),
        includePricing: Boolean(job.includePricing),
      })
      setToast(
        `Export started again${response?.jobId ? ` (Job ID ${response.jobId})` : ''}. Watching for progress…`,
      )
      setPage(0)
      await fetchJobs()
    } catch (err) {
      setToast(
        err.response?.data?.message ||
          'Could not restart this export. Check your connection and try again.',
      )
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/products/manage')}
            sx={{ mb: 1, color: '#e90000' }}
          >
            Back to Manage Products
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
            Product Downloads
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Track Excel exports. Ready files stay available for 7 days.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchJobs()}
          sx={{ color: '#e90000', borderColor: '#e90000' }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {hasActiveJobs && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Preparing your file… This page updates automatically while jobs are queued or preparing.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        {loading && jobs.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              No downloads yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start one from Manage Products → Download.
            </Typography>
            <Button
              component={RouterLink}
              to="/products/manage"
              variant="contained"
              sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#c00000' } }}
            >
              Go to Manage Products
            </Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Options</TableCell>
                <TableCell>Counts</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} hover>
                  <TableCell>
                    <Typography
                      component="code"
                      variant="caption"
                      title={job.id}
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        display: 'block',
                        maxWidth: 160,
                      }}
                    >
                      {job.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDateTime(job.createdAt)}</TableCell>
                  <TableCell>
                    <StatusChip status={job.status} displayStatus={job.displayStatus} />
                    {(job.displayStatus === 'no_data' || job.status === 'failed') &&
                      job.errorMessage && (
                      <Typography
                        variant="caption"
                        color={job.displayStatus === 'no_data' ? 'text.secondary' : 'error'}
                        sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                      >
                        {job.errorMessage}
                      </Typography>
                    )}
                    {job.status === 'expired' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, maxWidth: 280 }}
                      >
                        This file expired after 7 days. Start a new download from Manage Products.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{scopeSummary(job)}</TableCell>
                  <TableCell>{optionsSummary(job)}</TableCell>
                  <TableCell>
                    {job.status === 'completed' || job.displayStatus === 'ready'
                      ? `Products ${job.productCount || 0}` +
                        (job.includeBarcodes ? ` · Barcodes ${job.barcodeCount || 0}` : '') +
                        (job.includePricing ? ` · Inventory ${job.inventoryCount || 0}` : '')
                      : '—'}
                  </TableCell>
                  <TableCell>{formatDateTime(job.expiresAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {job.canRetry && (
                        <Button
                          size="small"
                          startIcon={
                            retryingId === job.id ? (
                              <CircularProgress size={14} />
                            ) : (
                              <ReplayIcon />
                            )
                          }
                          onClick={() => handleRetry(job)}
                          disabled={retryingId === job.id}
                        >
                          Try again
                        </Button>
                      )}
                      {showDownloadExcel ? (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            downloadingId === job.id ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : (
                              <DownloadIcon />
                            )
                          }
                          disabled={!job.canDownload || downloadingId === job.id}
                          onClick={() => handleDownload(job)}
                          sx={{
                            bgcolor: '#e90000',
                            '&:hover': { bgcolor: '#c00000' },
                            '&.Mui-disabled': { bgcolor: '#f5c0c0', color: '#fff' },
                          }}
                        >
                          Download Excel
                        </Button>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {jobs.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10) || 20)
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </TableContainer>

      <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
        Need a new export?{' '}
        <Link component={RouterLink} to="/products/manage" underline="hover">
          Open Manage Products
        </Link>
      </Typography>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default ProductDownloadsPage
